import dayjs from "dayjs";

type Gender = "male" | "female";
type Nationality = "domestic" | "foreign";

export function parseBirth(birth6: string) {
  const year = parseInt(birth6.slice(0, 2), 10);
  const month = parseInt(birth6.slice(2, 4), 10) - 1;
  const day = parseInt(birth6.slice(4, 6), 10);

  // 현재 연도 기준으로 세기 판별
  const nowYY = dayjs().year() % 100;
  const century = year <= nowYY ? 2000 : 1900;

  return dayjs(new Date(century + year, month, day));
}

/**
 * 생년월일 마스킹 (연도만 노출)
 * - 780731 → 1978년 **월 **일
 */
export function maskBirth(birth6?: string): string {
  if (!birth6 || birth6.length < 6) return "";
  const year = parseBirth(birth6).year();
  return `${year}년 **월 **일`;
}

/** 주민번호 7번째 자리(1~8) 계산 */
export function getKoreanId7thDigit(
  yyMMdd: string,
  gender: Gender,
  nationality: Nationality
): 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 {
  if (!/^\d{6}$/.test(yyMMdd)) {
    throw new Error("생년월일은 YYMMDD(6자리) 형식이어야 합니다.");
  }

  const now = dayjs();
  const currentYY = Number(now.format("YY")); // 현재 연도의 뒷자리
  const yy = Number(yyMMdd.slice(0, 2));
  const mmdd = yyMMdd.slice(2, 6);

  // 세기 판별 (현재 연도 기준)
  const fullYear = yy <= currentYY ? 2000 + yy : 1900 + yy;

  // 날짜 유효성 검증
  const date = dayjs(`${fullYear}${mmdd}`, "YYYYMMDD", true);
  if (!date.isValid()) {
    throw new Error("유효하지 않은 생년월일입니다.");
  }

  const is2000s = fullYear >= 2000;
  const isMale = gender === "male";
  const isDomestic = nationality === "domestic";

  if (isDomestic && !is2000s) return isMale ? 1 : 2;
  if (isDomestic && is2000s)  return isMale ? 3 : 4;
  if (!isDomestic && !is2000s) return isMale ? 5 : 6;
  return isMale ? 7 : 8;
}

export function getAgeType(birth6: string): "라이징" | "마스터" {
  // YYMMDD → YYYY-MM-DD 변환
  const year = parseInt(birth6.slice(0, 2), 10);
  const month = parseInt(birth6.slice(2, 4), 10) - 1; // dayjs는 0-indexed month
  const day = parseInt(birth6.slice(4, 6), 10);

  // 1900년대인지 2000년대인지 판별 (간단 예시: 현재 연도 기준으로 뒤에서 2자리 비교)
  const nowYear = dayjs().year() % 100;
  const century = year <= nowYear ? 2000 : 1900;
  const birth = dayjs(new Date(century + year, month, day));

  // 만 나이 계산
  const today = dayjs();
  let age = today.year() - birth.year();
  if (
    today.month() < birth.month() ||
    (today.month() === birth.month() && today.date() < birth.date())
  ) {
    age -= 1;
  }

  // 기준 분기
  return age >= 51 ? "마스터" : "라이징";
}

export function resolveClassName(
  hasClass: boolean,
  applyClass: string,
  otherReturnValue: string
): string {
  if (!hasClass) return otherReturnValue;
  return applyClass === "master" ? "마스터 클래스" : "라이징 클래스";
}

export function getZodiacImg(birth6: string): string {
  const year = parseBirth(birth6).year();

  const images = [
    "/icons/ic-mouse.webp",   
    "/icons/ic-cow.webp",    
    "/icons/ic-tiger.webp",   
    "/icons/ic-rabbit.webp", 
    "/icons/ic-dragon.webp", 
    "/icons/ic-snake.webp",   
    "/icons/ic-horse.webp",  
    "/icons/ic-goat.webp",   
    "/icons/ic-monkey.webp",  
    "/icons/ic-chicken.webp", 
    "/icons/ic-dog.webp",  
    "/icons/ic-pig.webp",    
  ];

  return images[(year + 8) % 12]; 
}

